<script lang="ts" setup>
import moment from 'moment'
import { RECHARGE_TYPE_NAME } from '@/utils/enum'

defineProps<{
  config: any
  records: any[]
  fetching: boolean
  ending: boolean
  page: number
  setInfiniteRef: (el: HTMLElement | null) => void
}>()

const TYPE = RECHARGE_TYPE_NAME

function isVip(item: any) {
  return [TYPE.SubscriptionDiscount, TYPE.Subscription].includes(item.rechargeType)
}

function isDiscountVip(item: any) {
  return item.rechargeType === TYPE.SubscriptionDiscount
}
</script>

<template>
  <div :ref="setInfiniteRef" class="novelyn-record-page h-100dvh overflow-scroll bg-n-bg-global">
    <BasicNavbar class="color-n-text-2">
      <div class="novelyn-nav-title">
        Transaction History
      </div>
    </BasicNavbar>

    <div v-if="!fetching && page === 1 && records.length === 0" class="mt-11 pt-15vh w-full text-center flex flex-col justify-center">
      <BizBlockEmpty :title="config.texts.noRecord" />
    </div>

    <div v-else-if="records.length" class="novelyn-record-card">
      <div
        v-for="item in records"
        :key="item.orderId"
        class="novelyn-rec-row"
      >
        <!-- 图标 chip -->
        <span
          class="rec-ico"
          :class="{ 'is-dark': item.rechargeType === TYPE.Subscription, 'is-lav': isDiscountVip(item) }"
        >
          <img v-if="isVip(item)" src="/images/novelyn/crown.svg" alt="VIP">
          <img v-else src="/images/novelyn/coin.svg" alt="Coins">
        </span>

        <!-- 标题 + 日期 -->
        <div class="rec-main">
          <div class="rec-title">
            <template v-if="isVip(item)">
              {{ item.rechargeType === TYPE.SubscriptionDiscount ? config.texts.discountedVip : config.texts.unlimitedVip }} · {{ item.cycle }}
            </template>
            <template v-else>
              +{{ item.coins }} {{ config.texts.coins }}
            </template>
          </div>
          <div class="rec-sub">
            {{ $d(new Date(item.rechargeTime), 'short') }}<template v-if="item.expireTime">
              · {{ config.texts.expireOn }} {{ moment(item.expireTime).format('LL') }}
            </template>
          </div>
        </div>

        <!-- 类型标签 -->
        <span
          class="rec-tag"
          :class="{ 'is-gold': item.rechargeType === TYPE.Subscription, 'is-lav': isDiscountVip(item) }"
        >
          {{ config.texts.rechargeTypeLabel(item.rechargeType) }}
        </span>
      </div>
    </div>

    <div v-if="ending && records.length" class="novelyn-rec-ending">
      — {{ config.texts.noMore }} —
    </div>
    <div
      v-else-if="fetching"
      class="py-15px flex items-center justify-center space-x-4 text-13px text-n-text-3"
      :class="{ 'mt-52px': records.length === 0 }"
    >
      <div class="animate-spin">
        <i-icon-park-outline-loading />
      </div>
      <p>{{ config.texts.loading }}</p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.novelyn-record-page {
  padding-bottom: 24px;
  background: var(--novelyn-bg);
}

.novelyn-nav-title {
  position: absolute;
  inset: 0 58px;
  display: grid;
  place-items: center;
  color: var(--novelyn-ink);
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 17px;
  font-weight: 700;
  pointer-events: none;
}

/* 列表卡（抵消 fixed 导航栏高度） */
.novelyn-record-card {
  margin: 52px 16px 0;
  padding: 4px 16px;
  border-radius: 20px;
  background: #fff;
  box-shadow: var(--novelyn-shadow-sm);
}

.novelyn-rec-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 13px 4px;

  & + & {
    border-top: 1px solid var(--novelyn-line);
  }
}

.rec-ico {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 13px;
  background: var(--novelyn-gold-soft);

  img {
    width: 19px;
    height: 19px;
    object-fit: contain;
  }

  &.is-dark {
    background: #3A2A33;
  }

  &.is-lav {
    background: var(--novelyn-lavender-soft);
  }
}

.rec-main {
  flex: 1;
  min-width: 0;
}

.rec-title {
  overflow: hidden;
  font-size: 14px;
  font-weight: 900;
  color: var(--novelyn-ink);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rec-sub {
  margin-top: 2px;
  overflow: hidden;
  font-size: 11px;
  font-weight: 700;
  color: var(--novelyn-ink-2);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rec-tag {
  flex-shrink: 0;
  padding: 3px 9px;
  font-size: 10px;
  font-weight: 800;
  color: var(--novelyn-rose-deep);
  white-space: nowrap;
  border: 1px solid var(--novelyn-line);
  border-radius: 999px;
  background: var(--novelyn-rose-faint);

  &.is-gold {
    color: #9A7A1B;
    border-color: #EBD9A8;
    background: var(--novelyn-gold-soft);
  }

  &.is-lav {
    color: #8E5FC4;
    border-color: #EEE2F7;
    background: var(--novelyn-lavender-soft);
  }
}

.novelyn-rec-ending {
  padding: 8px 0;
  font-size: 11px;
  font-weight: 700;
  color: var(--novelyn-ink-3);
  text-align: center;
}
</style>
